import { RefObject, useEffect, useRef, useState } from 'react';
const VISIBILITY_STATUS_CHANGE_EVENT = 'onVisibilityStatusChange';

const viewportRootObserverCallback = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
        const { target, isIntersecting } = entry;
        target.dispatchEvent(
            new CustomEvent(VISIBILITY_STATUS_CHANGE_EVENT, { detail: { isIntersecting } })
        );
    });
};
const viewportRootObserver = new IntersectionObserver(viewportRootObserverCallback, {
    threshold: [0],
});

/**
 * Notifies whenever the target element's visibility status in viewport changes
 * @param targetRef The target element whose visibility in viewport needs to be tracked
 * @param wait The minimum duration for which the target should be in viewport to notify the visibility state to true
 * @param notifyOnlyOnce Notify only when the target is in viewport for the first time. No further visibility changes will be pushed irrespective of target's position
 */
export const useIsVisibleInViewport = (
    targetRef: RefObject<HTMLElement>,
    wait: number = 0,
    notifyOnlyOnce: boolean = false
) => {
    const [isVisibleInViewport, setIsVisibleInViewport] = useState<boolean>(false);
    const didNotifyOnceOnVisible = useRef<boolean>(false);

    useEffect(() => {
        if (!targetRef?.current || !viewportRootObserver) {
            return () => {};
        }

        const target = targetRef.current;
        let timer: NodeJS.Timer = null;

        // callback to set the isVisible state according to the init config
        const setIsVisibleState = (isVisible: boolean) => {
            if (notifyOnlyOnce) {
                if (isVisible && !didNotifyOnceOnVisible.current) {
                    setIsVisibleInViewport(true);
                    didNotifyOnceOnVisible.current = true;
                    viewportRootObserver.unobserve(target);
                }
            } else {
                setIsVisibleInViewport(isVisible);
                didNotifyOnceOnVisible.current = true;
            }
        };

        // The callback to be called on intersection status changes
        const onIntersectionCallback = (isIntersecting: boolean) => {
            if (isIntersecting) {
                if (!wait) {
                    setIsVisibleState(true);
                } else if (!timer) {
                    timer = setTimeout(() => {
                        setIsVisibleState(true);
                        timer = null;
                    }, wait);
                }
            } else {
                setIsVisibleState(false);
                if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            }
        };

        const onVisibilityStatusChangeCb = (event: CustomEvent) => {
            const { isIntersecting } = event.detail;
            onIntersectionCallback(isIntersecting);
        };

        // Add custom event listener and start observing
        target.addEventListener(VISIBILITY_STATUS_CHANGE_EVENT, onVisibilityStatusChangeCb);
        viewportRootObserver.observe(target);

        // Clean up the observation and timer
        return () => {
            target.removeEventListener(VISIBILITY_STATUS_CHANGE_EVENT, onVisibilityStatusChangeCb);
            viewportRootObserver.unobserve(target);
            timer && clearTimeout(timer);
        };
    }, [targetRef, wait, notifyOnlyOnce]);

    return isVisibleInViewport;
};
